import { chunk } from 'lodash';
import { getTranslations } from 'next-intl/server';

import {
  execute,
  getSessionValue,
  hasSessionCookieValue,
  MenuDocument,
  StoreConfigDocument
} from '@/fo/api/magento';
import { loyaltyMindboxService } from '@/fo/api/mindbox';
import {
  FeatureSearchSuggestionBase,
  InputSearch
} from '@/fo/feature/search/suggestion/base';
import {
  UiAppFooterBase,
  footerData as defaultFooterData
} from '@/fo/ui/app/footer/base';
import { UiAppHeaderBase } from '@/fo/ui/app/header/base';
import { UiAppTapbarMobile } from '@/fo/ui/app/tapbar/mobile';
import { utilMapMenuData, MenuIdentifier } from '@/fo/util/map-menu-data';

import { mapToJsonLd } from '../helpers/mapToJsonLd';

import { defaultStoreConfig } from './mock';

export const IDENTIFIERS = [
  MenuIdentifier.Catalog,
  MenuIdentifier.Brands,
  MenuIdentifier.Diagnostic,
  MenuIdentifier.AboutSkin,
  MenuIdentifier.Mobile,
  MenuIdentifier.ToBuyers
];

export interface FeatureAppLayoutBaseProps {
  children?: React.ReactNode;
}

export async function FeatureAppLayoutBase({
  children
}: FeatureAppLayoutBaseProps) {
  const t = await getTranslations();

  const isAuthorized = await hasSessionCookieValue('AUTH_TOKEN');
  const city = getSessionValue('LOCATION_CITY');
  const customerEmail = await getSessionValue('CUSTOMER_EMAIL');
  const customerMindboxId = await getSessionValue('CUSTOMER_MINDBOX_ID');

  const [storeConfigData, cityValue, menuData, customerLoyaltyLevel] =
    await Promise.all([
      execute(StoreConfigDocument, {}),
      city,
      execute(MenuDocument, {
        identifiers: IDENTIFIERS
      }),
      isAuthorized
        ? loyaltyMindboxService.getLoyaltyLevel({
            email: customerEmail,
            ids: { bitrixClientId: customerMindboxId }
          })
        : null
    ]);

  const loyaltyBalance =
    customerLoyaltyLevel && 'balances' in customerLoyaltyLevel
      ? customerLoyaltyLevel.balances?.[0]?.available || 0
      : 0;

  const storeConfig =
    storeConfigData?.data?.storeConfig ?? defaultStoreConfig.storeConfig;

  const suggestionLimit = Number(storeConfig.search_suggestion_limit) || 5;

  if (!menuData?.data?.snowdogMenus) {
    throw new Error('Menu not found');
  }

  const menu = utilMapMenuData(menuData.data.snowdogMenus);

  const toBuyersMenu = menu.toBuyers.map((item) => ({
    title: item.label,
    href: item.href
  }));

  const toBuyers = chunk(toBuyersMenu, 4).map((item) => ({
    links: item
  }));

  const footerData = {
    ...defaultFooterData,
    contact: {
      phone: storeConfig.store_telephone ?? '',
      hours: storeConfig.store_schedule ?? '',
      email: storeConfig.recipient_email ?? ''
    },
    copyright: {
      text: [storeConfig.copyright ?? '']
    },
    links: {
      title: t('toBuyers'),
      columns: toBuyers
    }
  };

  const searchInput = <InputSearch autoFocus={false} />;

  const searchSuggestion = (
    <FeatureSearchSuggestionBase
      key="searchSuggestion"
      isAuthorized={isAuthorized}
      suggestionLimit={suggestionLimit}
    />
  );

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(mapToJsonLd(storeConfig))
        }}
      />
      <UiAppHeaderBase
        loyaltyBalance={loyaltyBalance}
        isAuthorized={isAuthorized}
        informer={storeConfig.default_title ?? ''}
        city={{
          label: cityValue
        }}
        rightLink={{
          href: '/where-to-buy',
          label: t('whereToBuy')
        }}
        brands={{
          naos: { href: '/' },
          bioderma: { href: '/bioderma' },
          esthederm: { href: '/esthederm' },
          etatPur: { href: '/etat_pur' }
        }}
        links={[
          { label: t('selectCare'), href: '/survey/diagnostic' },
          { label: t('aboutSkin'), href: '/about-skin/articles/' }
        ]}
        menu={{
          categories: menu.catalog,
          brands: menu.brands,
          diagnosticArticles: menu.diagnostic,
          skinArticles: menu.aboutSkin
        }}
        searchSuggestion={searchSuggestion}
      />
      {children}
      <UiAppFooterBase data={footerData} />
      <UiAppTapbarMobile
        isAuthorized={isAuthorized}
        loyaltyBalance={loyaltyBalance}
        menuProps={{
          items: menu.mobile,
          additionalItems: menu.brands,
          city: cityValue,
          infoItems: [
            { label: t('aboutCompany'), href: '/about/' },
            { label: t('whereToBuy'), href: '/where-to-buy' }
          ],
          contactInfo: {
            phone: storeConfig.store_telephone ?? '',
            hours: storeConfig.store_schedule ?? '',
            email: storeConfig.recipient_email ?? ''
          }
        }}
        searchInput={searchInput}
        searchSuggestion={searchSuggestion}
      />
    </>
  );
}
